import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const LandingContentAdmin = () => {
  const [activeTab, setActiveTab] = useState('content');
  const [content, setContent] = useState({
    about_title: '',
    about_description: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    years_in_business: 5,
    total_users: 1000,
    total_transactions: 5000,
    trading_volume: '$10M+',
  });

  const [testimonials, setTestimonials] = useState([]);
  const [newTestimonial, setNewTestimonial] = useState({
    client_name: '',
    client_title: '',
    testimonial_text: '',
    rating: 5,
  });

  const [faqs, setFaqs] = useState([]);
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: '',
    category: 'General',
  });

  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(`${API_BASE}/landing/content`, { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        setContent(data.content);
        setTestimonials(data.testimonials);
        setFaqs(data.faqs);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContent = async () => {
    try {
      const response = await fetch(`${API_BASE}/landing/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.content) {
          setContent(data.content);
        }
        fetchContent();
        alert('Content updated successfully!');
      }
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to save content');
    }
  };

  const handleAddTestimonial = async () => {
    try {
      const response = await fetch(`${API_BASE}/landing/testimonials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newTestimonial),
      });

      if (response.ok) {
        alert('Testimonial added successfully!');
        setNewTestimonial({ client_name: '', client_title: '', testimonial_text: '', rating: 5 });
        fetchContent();
      }
    } catch (error) {
      console.error('Error adding testimonial:', error);
      alert('Failed to add testimonial');
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      const response = await fetch(`${API_BASE}/landing/testimonials/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchContent();
      }
    } catch (error) {
      console.error('Error deleting testimonial:', error);
    }
  };

  const handleAddFaq = async () => {
    try {
      const response = await fetch(`${API_BASE}/landing/faq`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newFaq),
      });

      if (response.ok) {
        alert('FAQ added successfully!');
        setNewFaq({ question: '', answer: '', category: 'General' });
        fetchContent();
      }
    } catch (error) {
      console.error('Error adding FAQ:', error);
      alert('Failed to add FAQ');
    }
  };

  const handleDeleteFaq = async (id) => {
    if (!window.confirm('Are you sure?')) return;

    try {
      const response = await fetch(`${API_BASE}/landing/faq/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchContent();
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
    }
  };

  if (loading) {
    return <div className="p-8 text-white text-center">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Tabs */}
      <div className="flex gap-4 border-b border-amber-500/20">
        {[
          { id: 'content', label: 'Landing Content' },
          { id: 'testimonials', label: 'Testimonials' },
          { id: 'faqs', label: 'FAQs' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-4 font-semibold transition-all ${
              activeTab === tab.id
                ? 'text-amber-400 border-b-2 border-amber-400'
                : 'text-gray-400 hover:text-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content Tab */}
      {activeTab === 'content' && (
        <div className="space-y-6">
          <div>
            <label className="block text-gray-300 mb-2">About Title</label>
            <input
              type="text"
              className="input-field"
              value={content.about_title}
              onChange={(e) => setContent({ ...content, about_title: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">About Description</label>
            <textarea
              className="input-field h-32"
              value={content.about_description}
              onChange={(e) => setContent({ ...content, about_description: e.target.value })}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-gray-300 mb-2">Contact Email</label>
              <input
                type="email"
                className="input-field"
                value={content.contact_email}
                onChange={(e) => setContent({ ...content, contact_email: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Contact Phone</label>
              <input
                type="tel"
                className="input-field"
                value={content.contact_phone}
                onChange={(e) => setContent({ ...content, contact_phone: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Address</label>
              <input
                type="text"
                className="input-field"
                value={content.contact_address}
                onChange={(e) => setContent({ ...content, contact_address: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Years in Business</label>
              <input
                type="number"
                className="input-field"
                value={content.years_in_business}
                onChange={(e) => setContent({ ...content, years_in_business: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Total Users</label>
              <input
                type="number"
                className="input-field"
                value={content.total_users}
                onChange={(e) => setContent({ ...content, total_users: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Total Transactions</label>
              <input
                type="number"
                className="input-field"
                value={content.total_transactions}
                onChange={(e) => setContent({ ...content, total_transactions: parseInt(e.target.value) })}
              />
            </div>

            <div>
              <label className="block text-gray-300 mb-2">Trading Volume</label>
              <input
                type="text"
                className="input-field"
                value={content.trading_volume}
                onChange={(e) => setContent({ ...content, trading_volume: e.target.value })}
              />
            </div>
          </div>

          <button
            onClick={handleSaveContent}
            className="btn-primary w-full"
          >
            Save Content
          </button>
        </div>
      )}

      {/* Testimonials Tab */}
      {activeTab === 'testimonials' && (
        <div className="space-y-6">
          <div className="bg-slate-800/50 rounded-lg p-6 border border-amber-500/20">
            <h3 className="text-xl font-bold text-white mb-4">Add New Testimonial</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Client Name"
                className="input-field"
                value={newTestimonial.client_name}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, client_name: e.target.value })}
              />
              <input
                type="text"
                placeholder="Client Title"
                className="input-field"
                value={newTestimonial.client_title}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, client_title: e.target.value })}
              />
              <textarea
                placeholder="Testimonial Text"
                className="input-field h-24"
                value={newTestimonial.testimonial_text}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, testimonial_text: e.target.value })}
              />
              <select
                className="input-field"
                value={newTestimonial.rating}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, rating: parseInt(e.target.value) })}
              >
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{r} Stars</option>
                ))}
              </select>
              <button
                onClick={handleAddTestimonial}
                className="btn-primary w-full"
              >
                Add Testimonial
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Current Testimonials</h3>
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-slate-800/50 rounded-lg p-4 border border-amber-500/20">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-white">{testimonial.client_name}</p>
                    <p className="text-sm text-amber-300">{testimonial.client_title}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteTestimonial(testimonial.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
                <p className="text-gray-400 text-sm">{testimonial.testimonial_text}</p>
                <p className="text-yellow-300 text-sm mt-2">{'⭐'.repeat(testimonial.rating)}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAQs Tab */}
      {activeTab === 'faqs' && (
        <div className="space-y-6">
          <div className="bg-slate-800/50 rounded-lg p-6 border border-amber-500/20">
            <h3 className="text-xl font-bold text-white mb-4">Add New FAQ</h3>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Question"
                className="input-field"
                value={newFaq.question}
                onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
              />
              <textarea
                placeholder="Answer"
                className="input-field h-24"
                value={newFaq.answer}
                onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
              />
              <select
                className="input-field"
                value={newFaq.category}
                onChange={(e) => setNewFaq({ ...newFaq, category: e.target.value })}
              >
                <option>General</option>
                <option>Orders</option>
                <option>Payment</option>
                <option>Security</option>
                <option>Support</option>
              </select>
              <button
                onClick={handleAddFaq}
                className="btn-primary w-full"
              >
                Add FAQ
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-bold text-white">Current FAQs</h3>
            {faqs.map((faq) => (
              <div key={faq.id} className="bg-slate-800/50 rounded-lg p-4 border border-amber-500/20">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold text-white">{faq.question}</p>
                    <p className="text-sm text-amber-300">{faq.category}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteFaq(faq.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
                <p className="text-gray-400 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingContentAdmin;
